import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FiClock } from 'react-icons/fi';

import { Countdown } from 'components/countdown';
import Button from 'components/ui/button';
import { PROGRAM_BACKGROUND } from 'components/ui/program-panel';
import { useModal } from 'context/ModalContext';
import { MIN_BALANCE_TO_RECOGNIZE } from 'pages/Reconhecer/recognition-points';
import { FormatNumber } from 'utils/helpers';

import { DraftWarning } from './program-section';
import { RecentRecognitions, usePrefixRecognitions } from './recent-recognitions';
import { ArrowLink, SectionHeading } from './section-heading';

const TIERS = [
  { tag: '#VALEU', max: 100 },
  { tag: '#MANDOUBEM', max: 200 },
  { tag: '#UAU', max: 300 },
];

interface RecognizePanelProps {
  /** Saldo `point_behavior`. */
  balance: number;
  cycleIsOpen: boolean;
  cycleIsClosed: boolean;
  /** Fim do ciclo aberto, quando o saldo expira. */
  cycleFinish?: number;
  /** `recognition !== false`: a pessoa participa do reconhecimento neste ciclo. */
  recognitionEnabled: boolean;
  /** `visualizador` só consulta: vê o painel, sem a ação de reconhecer. */
  readOnly: boolean;
  hasPartialRecognition: boolean;
  /** Prefixo da pessoa: o painel lê do cache os últimos reconhecimentos dele (FEED-11). */
  prefixo?: number;
  /** O perfil ainda não chegou, então o prefixo é desconhecido: o feed já mostra a espera (FEED-16). */
  prefixoLoading?: boolean;
}

function Balance({ balance }: { balance: number }) {
  return (
    <p className="flex items-center gap-2.5">
      <span className="text-jj-label leading-snug text-content">
        Saldo para
        <br />
        enviar:
      </span>
      <b className="ml-auto font-display text-jj-h1 font-bold leading-none text-brand-blue">{FormatNumber(balance || 0)}</b>
    </p>
  );
}

function RecognizeAction({ hasPartialRecognition }: { hasPartialRecognition: boolean }) {
  const navigate = useNavigate();
  const goRecognize = () => navigate('/reconhecer');

  return (
    <div className="relative inline-flex">
      <Button
        variant="solido-destaque"
        size="sm"
        onClick={goRecognize}
        aria-label="Reconhecer colega. Abre modal para registrar o reconhecimento."
      >
        Reconhecer colega
      </Button>

      {hasPartialRecognition && (
        <DraftWarning
          message="Você possui um reconhecimento em preenchimento. Clique no botão para continuar."
          onClick={goRecognize}
        />
      )}
    </div>
  );
}

/**
 * "Reconheça seus colegas": o feed do prefixo no bloco rosa e, à direita, a coluna creme com o
 * saldo, os níveis, a expiração e as ações (HOME-18). O feed vem primeiro também no DOM.
 */
export function RecognizePanel({
  balance,
  cycleIsOpen,
  cycleIsClosed,
  cycleFinish,
  recognitionEnabled,
  readOnly,
  hasPartialRecognition,
  prefixo,
  prefixoLoading = false,
}: RecognizePanelProps) {
  const { showModal } = useModal();
  const recent = usePrefixRecognitions(prefixo);
  // Sem prefixo ainda, a espera começa junto com a do perfil, e não só quando a leitura do feed sai.
  const feedStatus = recent.status === 'idle' && prefixoLoading ? 'loading' : recent.status;
  const headingId = useId();

  const canUseBalance = cycleIsOpen && recognitionEnabled;
  const hasBalance = !isNaN(balance) && balance >= MIN_BALANCE_TO_RECOGNIZE;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <SectionHeading id={headingId}>Reconheça seus colegas</SectionHeading>

      {/* As duas colunas ficam sempre, para a coluna do saldo não esticar e pular quando o feed
          chega. Com o prefixo conhecido, o bloco rosa nunca fica em branco: mostra a espera, a
          lista, o aviso de falha ou o de prefixo sem reconhecimentos (FEED-13).
          D3: 30px, 18px e a coluna de 262px não têm degrau no Tailwind. */}
      <div className={`grid overflow-hidden rounded-jj-panel md:grid-cols-[minmax(0,1fr)_262px] ${PROGRAM_BACKGROUND.reconhecimento}`}>
        <div className="px-4 pb-7 pt-6 empty:hidden sm:px-[30px] md:empty:block">
          {feedStatus !== 'idle' && <RecentRecognitions items={recent.items} prefixo={prefixo} status={feedStatus} />}
        </div>

        <div className="flex flex-col gap-[18px] bg-brand-yellow-light px-3.5 pb-6 pt-[18px] text-content">
          <div className="flex flex-col gap-3 rounded-jj-lg bg-brand-white px-3.5 py-4">
            {!cycleIsClosed && <Balance balance={balance} />}

            {/* O nome acessível é o próprio texto visível (WCAG 2.5.3); os separadores são só para
                o leitor de tela, que sem eles leria "#VALEUAté 100 pontos". */}
            <button
              type="button"
              onClick={() => showModal('help-moedas-bb')}
              className="flex flex-col gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-blue-dark"
            >
              {TIERS.map(tier => (
                <span key={tier.tag} className="flex w-full items-center gap-2 text-jj-label font-bold text-brand-blue">
                  {tier.tag}
                  <span className="screen-reader-text">: </span>
                  <span className="ml-auto whitespace-nowrap rounded-jj-pill bg-brand-purple-light px-3 py-1 text-jj-label font-semibold text-brand-blue-dark">
                    Até {tier.max} pontos
                  </span>
                  <span className="screen-reader-text">, </span>
                </span>
              ))}
              <span className="screen-reader-text">saiba mais sobre os tipos de reconhecimento</span>
            </button>

            {cycleIsOpen && cycleFinish && (
              <p className="mt-1 flex items-start gap-2 text-jj-label leading-jj-body-sm">
                <FiClock aria-hidden="true" size={14} className="mt-px shrink-0 text-brand-blue" />
                <span>
                  Seu saldo atual expira em:
                  <br />
                  <b className="font-semibold text-brand-blue">
                    <Countdown urgentClassName="text-urgent" hideIcon deadline={new Date(cycleFinish)} />
                  </b>{' '}
                  · até {format(new Date(cycleFinish), 'dd/MM/yyyy')}
                </span>
              </p>
            )}
          </div>

          <div className="mt-auto flex flex-col items-center gap-3.5 text-center">
            {canUseBalance && !hasBalance && (
              <p className="text-sm">
                Você não possui saldo de pontos para reconhecer.
                <br />
                Aguarde até a próxima distribuição.
              </p>
            )}

            {cycleIsClosed && <p className="text-sm">Esse ciclo já foi encerrado.</p>}

            {(canUseBalance || cycleIsClosed) && (
              <ArrowLink to="/extrato?active=1&sub=SENDED">Ver meus reconhecimentos</ArrowLink>
            )}

            {canUseBalance && hasBalance && !readOnly && <RecognizeAction hasPartialRecognition={hasPartialRecognition} />}
          </div>
        </div>
      </div>
    </section>
  );
}
